import React from 'react';
import {
  Header,
  SpaceBetween,
  Box,
  TextContent,
  ColumnLayout,
  Button,
  Link,
  Alert,
  AnchorNavigation,
  AppLayout,
  ContentLayout,
  Grid,
  ExpandableSection,
  Tabs,
} from '@cloudscape-design/components';
import MermaidDiagram from '../../components/MermaidDiagram';
import StrandsLogo from '../../components/StrandsLogo';
import { useTheme } from '../../context/ThemeContext';
import { useModuleVisit } from '../../hooks/useAnalytics';

const DEFAULT_BANNER = "linear-gradient(135deg, rgb(71, 17, 118) 3%, rgb(131, 57, 157) 44%, rgb(149, 85, 182) 69%, rgb(145, 134, 215) 94%)";
const SUMMIT_BANNER_LIGHT = "center/cover url('/summit/2025_Summits_Kaltura_SessionPage_SessionThumbnail_1@2x.png')";
const SUMMIT_BANNER_DARK = "center/cover url('/summit/2025_Summits_Kaltura_SessionPage_SessionThumbnail_2.png')";

// Main StrandsAgentsPage content component
function StrandsAgentsPageContent() {
  const { isSummit, isDark } = useTheme();
  useModuleVisit('strands_agents');
  const banner = isSummit ? (isDark ? SUMMIT_BANNER_DARK : SUMMIT_BANNER_LIGHT) : DEFAULT_BANNER;
  return (
    <ContentLayout
      defaultPadding
      disableOverlap
      headerBackgroundStyle={banner}
      headerVariant="high-contrast"
      header={
        <Box padding={{ vertical: "xxxl" }}>
          <Grid
            gridDefinition={[
              { colspan: { default: 12, s: 8 } }
            ]}
          >
            <Box padding="s">
              <Box display="flex" alignItems="center" margin={{ bottom: "s" }}>
                <StrandsLogo width={120} height={35} />
              </Box>
              <Box
                fontSize="display-l"
                fontWeight="bold"
                variant="h1"
                padding="n"
              >
                Build production-ready,
                multi-agent AI systems
                in a few lines of code
              </Box>
              <Box
                variant="p"
                color="text-body-secondary"
                margin={{ top: "xs", bottom: "l" }}
              >
                ResearchIQ leverages the Strands Agents framework to orchestrate sophisticated 
                multi-agent workflows with deterministic execution patterns and enterprise-grade reliability.
              </Box>
            </Box>
          </Grid>
        </Box>
      }
    >
      <SpaceBetween size="xxl">
        {/* Benefits of Strands Agents Section */}
        <div id="strands-benefits">
          <Box margin={{ top: "xl", bottom: "l" }}>
            <hr style={{ 
              border: 'none', 
              borderTop: '1px solid #e9ebed', 
              margin: '3.5rem 0 3.5rem 0' 
            }} />
            <Header variant="h1" margin={{ bottom: "l" }}>
              Strands Agents SDK
            </Header>
          </Box>
          
          <ColumnLayout columns={2} variant="text-grid">
            <div>
              <TextContent>
                <h3>Modern Driven Orchestration</h3>
                <p>
                  Strands leverages model reasoning to plan, orchestrate tasks, and reflect on goals.
                </p>
                
                <h3>Model & Provider Agnostic</h3>
                <p>
                  Work with any LLM provider - Amazon Bedrock, OpenAI, Anthropic, local models. Switch providers without changing your code.
                </p>
              </TextContent>
            </div>
            
            <div>
              <TextContent>
                <h3>Simple Multi-Agent Primitives</h3>
                <p>
                  Simple primitives for handoffs, swarms, and graph workflows with built-in support for A2A.
                </p>
                
                <h3>Best In-Class AWS Integrations</h3>
                <p>
                  Native tools for AWS service interactions. Deploy easily into EKS, Lambda, EC2, and more. Native MCP tool integration.
                </p>
              </TextContent>
            </div>
          </ColumnLayout>
          
          <Box textAlign="center" margin={{ top: "l" }}>
            <SpaceBetween direction="horizontal" size="m">
              <Button 
                variant="primary" 
                href="https://strandsagents.com/latest/documentation/docs/user-guide/quickstart/" 
                target="_blank"
                iconAlign="right"
                iconName="external"
              >
                Visit SDK Documentation
              </Button>
              <Button 
                variant="primary" 
                href="https://catalog.workshops.aws/strands-agents/en-US" 
                target="_blank"
                iconAlign="right"
                iconName="external"
              >
                Start Workshop
              </Button>
            </SpaceBetween>
          </Box>
        </div>

        {/* Agent Loop Section */}
        <div id="agent-loop">
          <Box margin={{ top: "xl", bottom: "l" }}>
            <hr style={{ 
              border: 'none', 
              borderTop: '1px solid #e9ebed', 
              margin: '3.5rem 0 3.5rem 0' 
            }} />
            <Header variant="h1" margin={{ bottom: "l" }}>
              Agents Concepts
            </Header>
          </Box>
          
          <TextContent>
            <p>
              The agent loop is a core concept in the Strands Agents SDK that enables intelligent, autonomous behavior 
              through a cycle of reasoning, tool use, and response generation. This document explains how the agent loop 
              works, its components, and how to effectively use it in your applications.
            </p>

            <h2 id="agent-loop">What is the Agent Loop?</h2>
            <p>
              The agent loop is the process by which a Strands agent processes user input, makes decisions, executes tools, 
              and generates responses. It's designed to support complex, multi-step reasoning and actions with seamless 
              integration of tools and language models.
            </p>

            <Box margin={{ vertical: "m" }}>
              <MermaidDiagram
                id="agent-loop-flow"
                chart={`
                  flowchart LR
                    A[Input & Context] --> Loop

                    subgraph Loop[" "]
                        direction TB
                        B["Reasoning (LLM)"] --> C["Tool Selection"]
                        C --> D["Tool Execution"]
                        D --> B
                    end

                    Loop --> E[Response]
                `}
              />
            </Box>

            <p>At its core, the agent loop follows these steps:</p>
            <ol>
              <li><strong>Receives user input</strong> and contextual information</li>
              <li><strong>Processes the input</strong> using a language model (LLM)</li>
              <li><strong>Decides</strong> whether to use tools to gather information or perform actions</li>
              <li><strong>Executes tools</strong> and receives results</li>
              <li><strong>Continues reasoning</strong> with the new information</li>
              <li><strong>Produces a final response</strong> or iterates again through the loop</li>
            </ol>

            <p>
              This cycle may repeat multiple times within a single user interaction, allowing the agent to perform 
              complex, multi-step reasoning and autonomous behavior.
            </p>
          </TextContent>
        </div>

        {/* Graph Multi-Agent Pattern Section */}
        <div id="graph-pattern">
          <Box margin={{ top: "xl", bottom: "l" }}>
            <hr style={{ 
              border: 'none', 
              borderTop: '1px solid #e9ebed', 
              margin: '3.5rem 0 3.5rem 0' 
            }} />
            <Header variant="h1" margin={{ bottom: "l" }}>
              Graph Multi-Agent Pattern
            </Header>
          </Box>
          
          <TextContent>
            <p>
              A Graph is a deterministic Directed Acyclic Graph (DAG) based agent orchestration system where agents, 
              custom nodes, or other multi-agent systems (like Swarm or nested Graphs) are nodes in a graph. 
              Nodes are executed according to edge dependencies, with output from one node passed as input to connected nodes.
            </p>
            
            <h2 id="how-graphs-work" style={{ marginTop: '3rem' }}>How Graphs Work</h2>
            <p>The Graph pattern operates on the principle of structured, deterministic workflows where:</p>
            <ol>
              <li>Nodes represent agents, custom nodes, or multi-agent systems</li>
              <li>Edges define dependencies and information flow between nodes</li>
              <li>Execution follows a topological sort of the graph</li>
              <li>Output from one node becomes input for dependent nodes</li>
              <li>Entry points receive the original task as input</li>
            </ol>

            <Box margin={{ vertical: "m" }}>
              <MermaidDiagram
                id="research-workflow"
                chart={`
                  graph TD
                    A[Document Researcher] --> B[Evidence Compiler]
                    B --> C[Citation Tracker]
                    C --> D{Results Complete}
                    D --> E[Strategic Insights Agent]
                    D --> F[Exec Brief Agent ×4 parallel]
                    
                    style A fill:#232f3e,stroke:#fff,stroke-width:2px,color:#fff
                    style B fill:#ff9900,stroke:#fff,stroke-width:2px,color:#fff
                    style C fill:#146eb4,stroke:#fff,stroke-width:2px,color:#fff
                    style D fill:#545b64,stroke:#fff,stroke-width:2px,color:#fff
                    style E fill:#1d8348,stroke:#fff,stroke-width:2px,color:#fff
                    style F fill:#8b5cf6,stroke:#fff,stroke-width:2px,color:#fff
                `}
              />
            </Box>

            <p>ResearchIQ's multi-agent pipeline processes 6 questions in parallel, each through:</p>
            <ol>
              <li><strong>Document Researcher</strong> — Knowledge Base retrieval and data extraction (Haiku 4.5)</li>
              <li><strong>Evidence Compiler</strong> — Cross-document correlation and pattern analysis (Nova Pro)</li>
              <li><strong>Citation Tracker</strong> — Source verification and markdown formatting (Nova Pro)</li>
            </ol>
            <p>After all questions complete:</p>
            <ol start="4">
              <li><strong>Strategic Insights Agent</strong> — Cross-question theme detection, risk identification (Nova Pro, async)</li>
              <li><strong>Exec Brief Agent</strong> — 4 sections generated in parallel: Summary, SWO, Financials, Risk (GPT-OSS 120B)</li>
            </ol>

            <div id="agent-configuration">
              <ExpandableSection headerText="Agent Configuration Details" variant="container" headerDescription="Detailed system prompts and configuration for each agent">
              <Tabs
                tabs={[
                  {
                    label: "Document Researcher",
                    id: "document-researcher",
                    content: (
                      <Box margin={{ vertical: "m" }}>
                        <pre style={{ 
                          backgroundColor: "var(--color-background-container-content-6u8rvp, #f8f9fa)", 
                          padding: "1rem", 
                          borderRadius: "4px", 
                          overflow: "auto",
                          fontSize: "0.875rem",
                          lineHeight: "1.4"
                        }}>
{`# Document Researcher Agent Configuration

## System Prompt
You are a Document Researcher. Find specific information in financial documents using the retrieve tool.

Tasks:
- Search knowledge base for precise data points and metrics
- Extract exact quotes with source attribution
- Provide direct answers without analysis or interpretation
- If initial search yields poor results, try alternative search terms

Search Strategy:
- Start with the exact terms from the question
- If no good results (low scores or irrelevant content), try 1-2 alternative searches with:
  * Broader related terms
  * Synonyms or alternative phrasings
  * Industry-specific terminology
- For AWS/cloud questions: try "Amazon Web Services", "cloud services", "web services"
- For strategy questions: try "strategic initiatives", "business plans", "growth plans"
- For ESG topics: try "sustainability", "environmental", "governance", "social responsibility"

Important Guidelines:
- DO NOT get stuck in endless search loops
- If after 2-3 searches you don't find relevant information, clearly state what you searched for
- Focus on the BEST available information rather than perfect matches
- Provide partial answers if you find related but not exact information

Citation Attribution Types:
For each piece of information, indicate how it was derived:
- **[V]** = Verbatim: Quoted directly from the source document without changes
- **[P]** = Paraphrased: Rephrased or summarized from the source document
- **[C]** = Computed: Calculated from multiple data points in the document

For computed values, include the calculation logic to build confidence:
- Example: "11% growth [C: ($638B - $575B) / $575B = 11%]"`}
                        </pre>
                      </Box>
                    )
                  },
                  {
                    label: "Evidence Compiler",
                    id: "evidence-compiler",
                    content: (
                      <Box margin={{ vertical: "m" }}>
                        <pre style={{ 
                          backgroundColor: "var(--color-background-container-content-6u8rvp, #f8f9fa)", 
                          padding: "1rem", 
                          borderRadius: "4px", 
                          overflow: "auto",
                          fontSize: "0.875rem",
                          lineHeight: "1.4"
                        }}>
{`# Evidence Compiler Agent Configuration

## System Prompt
You are an Evidence Compiler. Analyze research findings to identify patterns, correlations, and organize evidence for professional reporting.

Core Tasks:
1. **Pattern Analysis**: Identify the most significant patterns and trends in the research data (focus on 2-5 key patterns)
2. **Gap Identification**: Note contradictions, missing information, or data inconsistencies  
3. **Thematic Organization**: Group related findings by business themes or topics with appropriate detail
4. **Citation Preparation**: Ensure all findings retain their source attributions for final formatting
5. **Quality Analysis**: Provide detailed analysis that covers the essential aspects without unnecessary elaboration

Analysis Framework:

### Pattern Recognition:
- Look for trends across multiple data points or time periods
- Identify correlations between different metrics or findings
- Note recurring themes or consistent messages across sources

### Quality Assessment:
- Flag any contradictory information between sources
- Identify gaps where additional information would be valuable
- Note the strength/confidence level of different findings based on source quality

### Thematic Grouping:
Organize findings into logical business categories such as:
- **Financial Performance**: Revenue, profitability, growth metrics
- **Strategic Initiatives**: Business plans, market expansion, partnerships
- **Operational Metrics**: Efficiency, productivity, operational changes
- **Market Position**: Competitive analysis, market share, industry trends
- **Risk Factors**: Challenges, threats, regulatory issues`}
                        </pre>
                      </Box>
                    )
                  },
                  {
                    label: "Citation Tracker",
                    id: "citation-tracker",
                    content: (
                      <Box margin={{ vertical: "m" }}>
                        <pre style={{ 
                          backgroundColor: "var(--color-background-container-content-6u8rvp, #f8f9fa)", 
                          padding: "1rem", 
                          borderRadius: "4px", 
                          overflow: "auto",
                          fontSize: "0.875rem",
                          lineHeight: "1.4"
                        }}>
{`# Citation Tracker Agent Configuration

## System Prompt
You are a Citation Tracker. Create the final polished report with proper markdown formatting and consistent citations.

Your role is to transform research findings into a professional, well-structured markdown document that renders beautifully in web interfaces.

Core Tasks:
1. **Markdown Formatting**: Use proper markdown syntax for headers, lists, emphasis, and code blocks
2. **Citation Consistency**: Ensure every fact has standardized source attribution
3. **Professional Structure**: Organize content with clear hierarchy and flow
4. **Reference Management**: Create comprehensive, properly formatted reference list

Markdown Formatting Requirements:

### Headers and Structure:
- Use ## Executive Summary for main sections
- Use ### Key Findings for subsections  
- Use #### Supporting Details for sub-subsections
- Never use single # headers (reserved for document title)

### Citations and References:
- Use consistent citation format: [Amazon 2024 Annual Report, p.15]
- Always use clean document names (not S3 URLs or full paths)
- Include inline citations within sentences
- Preserve attribution indicators: Keep [V], [P], [C] tags from previous stages
- Example: Revenue was $638B [V] [Amazon 2024 Annual Report, p.1]

### Professional Formatting:
- Use **bold** for key metrics and important findings
- Use *italics* for document names and emphasis
- Use > for blockquotes and key takeaways
- Use horizontal rules --- to separate major sections

Key Takeaway Requirements:
- Create compelling, concise summary (1-2 sentences) that captures research essence
- Focus on investment significance and business implications
- MUST use blockquote formatting: > **Key Takeaway**: [Your summary here]
- Place at the very end after references section`}
                        </pre>
                      </Box>
                    )
                  }
                ]}
              />
              </ExpandableSection>
            </div>

            <hr style={{ 
              border: 'none', 
              borderTop: '1px solid #e9ebed', 
              margin: '3rem 0 2rem 0' 
            }} />
            <h2 id="graph-topologies" style={{ marginBottom: '2rem' }}>Common Graph Topologies</h2>
            
            <h3>1. Sequential Pipeline</h3>
            <Box margin={{ vertical: "m" }}>
              <MermaidDiagram
                id="sequential-pipeline"
                chart={`
                  graph LR
                    A[Research] --> B[Analysis] --> C[Review] --> D[Report]
                `}
              />
            </Box>

            <h3>2. Parallel Processing with Aggregation</h3>
            <Box margin={{ vertical: "m" }}>
              <MermaidDiagram
                id="parallel-processing"
                chart={`
                  graph TD
                    A[Coordinator] --> B[Worker 1]
                    A --> C[Worker 2]
                    A --> D[Worker 3]
                    B --> E[Aggregator]
                    C --> E
                    D --> E
                `}
              />
            </Box>

            <h3>3. Branching Logic</h3>
            <Box margin={{ vertical: "m" }}>
              <MermaidDiagram
                id="branching-logic-diagram"
                chart={`
                  graph TD
                    A[Classifier] --> B[Technical Branch]
                    A --> C[Business Branch]
                    B --> D[Technical Report]
                    C --> E[Business Report]
                `}
              />
            </Box>
          </TextContent>
        </div>
      </SpaceBetween>
    </ContentLayout>
  );
}

// Tools component for "On this page" navigation
function StrandsAgentsPageTools() {
  return (
    <div>
      <Header variant="h3">
        <span id="navigation-header">On this page</span>
      </Header>
      <AnchorNavigation
        anchors={[
          {
            text: "What is the Agent Loop?",
            href: "#agent-loop",
            level: 1
          },
          {
            text: "How Graphs Work",
            href: "#how-graphs-work",
            level: 1
          },
          {
            text: "Agent Configuration Details",
            href: "#agent-configuration",
            level: 1
          },
          {
            text: "Common Graph Topologies",
            href: "#graph-topologies",
            level: 1
          }
        ]}
        ariaLabelledby="navigation-header"
      />
    </div>
  );
}

function StrandsAgentsPage() {
  return (
    <AppLayout
      content={<StrandsAgentsPageContent />}
      tools={<StrandsAgentsPageTools />}
      toolsHide={false}
      navigationHide={true}
    />
  );
}

export default StrandsAgentsPage;
